import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, Calendar, Check, Loader2, CreditCard, X } from "lucide-react";
import { PixCheckoutDialog } from "@/components/PixCheckoutDialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

const PLANS = {
  FREE: {
    name: "Gratuito",
    price: 0,
    priceId: null,
    features: [
      "Até 10 clientes",
      "Até 10 empréstimos",
      "Dashboard básico",
    ],
  },
  PRO: {
    name: "PRO",
    price: 29.90,
    priceId: "price_1SSKPxPF9BTevTU0ETiCbXHR",
    features: [
      "Clientes ilimitados",
      "Empréstimos ilimitados",
      "Dashboard analítico completo",
      "Relatórios avançados",
      "Suporte prioritário",
    ],
  },
};

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<{
    subscribed: boolean;
    plan: string;
    subscription_end: string | null;
  } | null>(null);

  useEffect(() => {
    // Handle success/cancel from Stripe
    if (searchParams.get("success") === "true") {
      toast.success("Assinatura realizada com sucesso!");
      // Refresh subscription status
      setTimeout(() => checkSubscription(), 1000);
    } else if (searchParams.get("canceled") === "true") {
      toast.info("Assinatura cancelada");
    }
  }, [searchParams]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
          navigate("/auth");
          return;
        }
        setUser(session.user);
        await checkSubscription();
      } catch (err) {
        console.error('Session error:', err);
        navigate("/auth");
      }
    };

    checkSession();

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
    });

    return () => authSub.unsubscribe();
  }, [navigate]);

  const checkSubscription = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('Check subscription error:', error);
        setSubscriptionData({ subscribed: false, plan: "FREE", subscription_end: null });
        return;
      }
      
      setSubscriptionData(data);
    } catch (err) {
      console.error('Check subscription error:', err);
      setSubscriptionData({ subscribed: false, plan: "FREE", subscription_end: null });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (priceId: string) => {
    try {
      setCheckoutLoading(true);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error("Erro ao iniciar checkout. Tente novamente.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) {
        throw new Error(error.message);
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Portal error:', err);
      toast.error("Erro ao abrir portal. Tente novamente.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentPlan = subscriptionData?.plan || "FREE";
  const isPro = currentPlan === "PRO";
  const expiresAt = subscriptionData?.subscription_end ? new Date(subscriptionData.subscription_end) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background p-4">
      <div className="container mx-auto max-w-5xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>

        <div className="space-y-8">
          {/* Current Subscription Status */}
          {isPro && (
            <Card className="glass border-primary/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-500" />
                      Assinatura Ativa
                    </CardTitle>
                    <CardDescription>Você está no plano PRO</CardDescription>
                  </div>
                  <Badge className="bg-gradient-to-r from-amber-500 to-amber-600">
                    PRO
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  {expiresAt && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>
                        Próxima renovação: {format(expiresAt, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="gap-2"
                  >
                    {portalLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Gerenciar Assinatura
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plans Grid */}
          <div>
            <h2 className="text-2xl font-bold mb-6 text-center">Escolha seu plano</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Free Plan */}
              <Card className={`glass relative ${currentPlan === "FREE" ? "border-primary/50" : ""}`}>
                {currentPlan === "FREE" && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                    Plano Atual
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{PLANS.FREE.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">R$ 0</span>
                    <span className="text-muted-foreground">/mês</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {PLANS.FREE.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled
                  >
                    {currentPlan === "FREE" ? "Plano Atual" : "Gratuito"}
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan */}
              <Card className={`glass relative ${isPro ? "border-amber-500/50" : "border-primary/30"}`}>
                {isPro && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-amber-600">
                    Plano Atual
                  </Badge>
                )}
                {!isPro && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                    Recomendado
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl flex items-center justify-center gap-2">
                    <Crown className="h-5 w-5 text-amber-500" />
                    {PLANS.PRO.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">R$ 29,90</span>
                    <span className="text-muted-foreground">/mês</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {PLANS.PRO.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isPro ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                    >
                      {portalLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Gerenciar
                    </Button>
                  ) : (
                    <PixCheckoutDialog
                      amount={PLANS.PRO.price}
                      description="Assinatura PRO - Gestão de Empréstimos"
                      trigger={
                        <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                          Assinar Agora
                        </Button>
                      }
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={checkSubscription}
              disabled={loading}
              className="text-muted-foreground"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Atualizar status da assinatura
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
